import * as Sentry from "@sentry/react";
import Hls from 'hls.js';
import { useMemo, useState } from 'react';
import { VideoTexture } from 'three';
import { addMediaElementToAudioStreamDestination } from '../utils/audio-manager';
import { ManagedBufferSource } from '../utils/ManagedBufferSource';
export const autoplayableVideos = [];
export const createAutoplayVideoNodes = () => {
    for (let index = 0; index < 10; index++) {
        const video = document.createElement('video');
        video.setAttribute('playsinline', 'true');
        video.setAttribute('webkit-playsinline', 'true');
        video.setAttribute('crossorigin', 'anonymous');
        video.crossOrigin = 'anonymous';
        video.muted = true;
        autoplayableVideos.push(video);
    }
};
// TODO: should this be changed?
export const unmuteAutoplayVideos = () => {
    autoplayableVideos.forEach((video) => (video.muted = false));
};
// cache for texture data
export const hlsVideoCache = {};
let hlsVideoCacheHasBeenUnmuted = false;
export const unmuteVideosInCache = () => {
    if (!hlsVideoCacheHasBeenUnmuted) {
        hlsVideoCacheHasBeenUnmuted = true;
        for (const id in hlsVideoCache) {
            // If there is an audiobuffer it means video audio is managed separately from the video.
            // The video remains always muted and we only want to unmute the separate audio buffer.
            const audioBuffer = hlsVideoCache[id][3];
            if (audioBuffer) {
                audioBuffer.muted = false;
                continue;
            }
            const video = hlsVideoCache[id][1];
            if (video)
                video.muted = false;
        }
    }
};
const syncAudio = (videoEl, bufferSourceNode) => {
    try {
        if (!videoEl.paused && bufferSourceNode.paused) {
            bufferSourceNode.play(videoEl.currentTime);
        }
        else if (videoEl.paused && !bufferSourceNode.paused) {
            bufferSourceNode.stop();
        }
        // Sync timing if they drift apart (only when both are playing)
        if (!videoEl.paused && !bufferSourceNode.paused) {
            const timeDiff = Math.abs(bufferSourceNode.currentTime - videoEl.currentTime);
            if (timeDiff > 0.3) {
                // Allow 300ms drift before resync
                // console.log(`🔄 Resyncing audio: diff=${timeDiff.toFixed(2)}s; videoTime: ${videoEl.currentTime}, audioTime: ${bufferSourceNode.currentTime}`);
                bufferSourceNode.play(videoEl.currentTime);
            }
        }
    }
    catch (error) {
        console.warn('Sync error:', error);
    }
};
const createAudioBuffer = async (videoUrl, audioContext) => {
    const baseUrl = videoUrl.replace(/\/[^\/]*\.m3u8.*$/, '');
    const audioUrl = `${baseUrl}/audio.m4a`;
    let audioBuffer = null;
    try {
        const response = await fetch(audioUrl);
        const arrayBuffer = await response.arrayBuffer();
        audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    }
    catch (error) {
        Sentry.captureException(error);
        console.warn('Error loading audio data: returning null for audio buffer', error);
    }
    return audioBuffer;
};
// cache for texture errors
const errorCache = {};
export const useHlsLoader = (url, id, ext) => {
    const [_, triggerRender] = useState(0);
    // if video loading prevented return early
    if (ext?.preventVideoLoad)
        return [undefined, undefined, undefined, null];
    // if async error throw error into render phase (triggered via triggerRender)
    if (errorCache[id]) {
        const error = errorCache[id];
        // delete errorCache[id]; // leads to re-render issue if not commented out
        throw error;
    }
    // use cached data if exists otherwise throw promise
    if (hlsVideoCache[id]) {
        // restart loading when cache accessed if autoplay enabled & hls exists
        if (ext?.autoplay) {
            // if (cache[id][1]) cache[id][1]!.autoplay = true;
            hlsVideoCache[id][2]?.startLoad();
        }
        return hlsVideoCache[id];
    }
    const { errorMessage = 'failed to load video', loop = false, 
    // muted = false,
    autoplay = false, onLoadedData, } = ext || {};
    // set up video
    let video;
    if (autoplay && autoplayableVideos.length) {
        video = autoplayableVideos[autoplayableVideos.length - 1];
        autoplayableVideos.splice(-1);
    }
    else {
        video = document.createElement('video');
    }
    // logAllVideoEvents(video)
    video.id = id;
    video.crossOrigin = 'anonymous';
    video.setAttribute('playsinline', 'true');
    video.setAttribute('webkit-playsinline', 'true');
    if (ext?.addVideoToDom) {
        video.style.width = '1px';
        video.style.height = '1px';
        video.style.position = 'absolute';
        video.style.zIndex = '-99999';
        document.body.appendChild(video);
    }
    const setSize = () => {
        video.width = video.videoWidth;
        video.height = video.videoHeight;
    };
    video.addEventListener('loadeddata', setSize);
    video.addEventListener('loadedmetadata', setSize);
    video.addEventListener('canplaythrough', setSize);
    if (onLoadedData)
        video.addEventListener('loadeddata', onLoadedData);
    // if (autoplay) video.autoplay = autoplay;
    if (loop)
        video.loop = loop;
    if (!autoplay)
        video.muted = true;
    const cacheError = (data) => {
        ext?.onError?.();
        if (data)
            console.error('hls error: ', data);
        errorCache[id] = new Error(`hls error: ${data?.details || errorMessage}`);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        errorCache[id].hlsError = data;
        triggerRender((prevState) => prevState + 1);
    };
    const setupSeparateAudioSync = async (media) => {
        if (!ext?.audioContext || !ext?.audioStreamDestination)
            return null;
        let syncInterval = 0;
        let videoReady = false;
        let hasStartedAudio = false;
        video.muted = true;
        const audioBuffer = await createAudioBuffer(url, ext.audioContext);
        if (!audioBuffer) {
            // If no audio buffer, fallback to add video directly to audio context as separate management of audio not needed
            addMediaElementToAudioStreamDestination(ext?.audioContext, ext?.audioStreamDestination, media);
            return null;
        }
        const managedBufferSource = new ManagedBufferSource(ext?.audioContext, ext?.audioStreamDestination, audioBuffer);
        const playAudio = () => {
            try {
                if (!hasStartedAudio) {
                    hasStartedAudio = true;
                    managedBufferSource.play(video.currentTime);
                }
            }
            catch (error) {
                console.warn('Play handler error:', error);
            }
            // Continuous sync monitoring with error handling
            if (syncInterval === 0)
                syncInterval = window.setInterval(syncAudio.bind(this, video, managedBufferSource), 200);
        };
        video.addEventListener('canplay', () => {
            videoReady = true;
            playAudio();
        });
        video.addEventListener('play', () => {
            if (!videoReady) {
                console.warn('Video not ready yet; delaying audio sync');
                return;
            }
            playAudio();
        });
        video.addEventListener('pause', () => {
            try {
                managedBufferSource.stop();
            }
            catch (error) {
                console.warn('Pause handler error:', error);
            }
            clearInterval(syncInterval);
        });
        video.addEventListener('seeked', () => {
            try {
                managedBufferSource.play(video.currentTime);
            }
            catch (error) {
                console.warn('Seek handler error:', error);
            }
        });
        video.addEventListener('volumechange', () => {
            video.muted = true;
        });
        return managedBufferSource;
    };
    // throw promise for suspense to catch
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    throw new Promise((res) => {
        // logAllVideoEvents(video);
        let hls = undefined;
        let audioBufferSource = null;
        if (Hls.isSupported() && url.endsWith('m3u8')) {
            hls = new Hls({
                autoStartLoad: true,
                // Enable audio support in HLS.js
                enableWorker: true,
            });
            hls.startLevel = -1; // automatic start level selection
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            hls.on(Hls.Events.ERROR, (e, data) => {
                if (!data.fatal)
                    return;
                cacheError(data);
            });
            hls.once(Hls.Events.MEDIA_ATTACHED, async (_name, { media }) => {
                if (!ext?.audioContext || !ext?.audioStreamDestination)
                    return;
                if (ext?.separateAudioFromVideo) {
                    const bufferSource = await setupSeparateAudioSync(media);
                    if (!!hlsVideoCache[id] && !hlsVideoCache[id][3]) {
                        hlsVideoCache[id][3] = bufferSource;
                    }
                }
                else {
                    addMediaElementToAudioStreamDestination(ext?.audioContext, ext?.audioStreamDestination, media);
                }
            });
            hls.attachMedia(video);
            hls.loadSource(url);
            // If HLS lib not supported (iOS safari) and separate audio required, set up separate audio sync without hls.js
        }
        else if (ext?.separateAudioFromVideo && url.endsWith('m3u8')) {
            video.src = url;
            setupSeparateAudioSync(video).then(bufferSource => {
                if (!!hlsVideoCache[id] && !hlsVideoCache[id][3]) {
                    hlsVideoCache[id][3] = bufferSource;
                }
            });
            video.addEventListener('error', () => cacheError());
            // If not an HLS url, use native html element without HLS
        }
        else {
            video.src = url;
            if (ext?.audioContext && ext?.audioStreamDestination)
                addMediaElementToAudioStreamDestination(ext?.audioContext, ext?.audioStreamDestination, video);
            video.addEventListener('error', () => cacheError());
        }
        // We need to 'preload' videos until they can play so that "video.play().then(() => video.muted = false)" which
        // runs later on on user interaction and allows to run multiple soundtracks (audio/video) simultaneously works reliably.
        // If videos aren't preloaded video.play() doesn't resolve immediately (async) thus unmuting the video is outside the user interaction flow
        // and violating the safari multiple soundtrack permissions. Autoplay videos have already been preloaded when the video nodes were created and need to be ommitted in
        // the preloading to avoid video sounds becoming audible for a split second (autoplay videos are unmuted on launch)
        try {
            if (!autoplay)
                video.play().then(() => video.pause());
        }
        catch (error) {
            console.warn(error);
        }
        const videoTexture = new VideoTexture(video);
        hlsVideoCache[id] = [videoTexture, video, hls, audioBufferSource];
        // resolve promise when video playable, using loadedmetadata as canplay
        // event not fired in safari
        video.addEventListener('loadedmetadata', res);
    });
};
// access cached hls data
export const useGetHlsCache = (id) => useMemo(() => hlsVideoCache[id], [id]);
// remove cached hls data
export const useRemoveHlsCache = (id) => useMemo(() => {
    if (hlsVideoCache[id])
        delete hlsVideoCache[id];
    if (errorCache[id])
        delete errorCache[id];
}, [id]);
export const clearHlsCacheById = (id) => {
    if (hlsVideoCache[id])
        delete hlsVideoCache[id];
    if (errorCache[id])
        delete errorCache[id];
};
// export const useHlsPreloader = (hlsPreloadData: IHlsPreloadData[]): Promise<any> => {
// 	let promises: Promise<any>[] = [];
// 	for (let i = 0; i < hlsPreloadData.length; i++) {
// 		const { url, id, muted, loop, autoplay} = hlsPreloadData[i];
// 		let video: HTMLVideoElement;
// 		if (autoplay && autoplayableVideos.length) {
// 			video = autoplayableVideos[autoplayableVideos.length - 1];
// 			autoplayableVideos.splice(-1)
// 		} else {
// 			video = document.createElement('video')
// 		}
// 		video.id = id;
// 		video.crossOrigin = 'anonymous';
// 		video.setAttribute('playsinline', 'true');
// 		video.setAttribute('webkit-playsinline', 'true');
// 		//if (autoplay) video.autoplay = autoplay;
// 		if (loop) video.loop = true;
// 		// if (muted) video.muted = true;
// 		promises.push(new Promise<any>(res => {
// 			let hls: Hls | undefined = undefined;
// 			if (Hls.isSupported()) {
// 				hls = new Hls({ autoStartLoad: true });
// 				hls.startLevel = -1; // automatic start level selection
// 				hls.attachMedia(video);
// 				hls.loadSource(url);
// 				hls.on(Hls.Events.ERROR, (e, data) => {
// 					// // populate error cache and resolve
// 					if (!data.fatal) return;
// 					if (data) console.error('hls error: ', data)
// 					errorCache[id] = new Error(`hls error: ${data?.details || 'failed to load video'}`);
// 					(errorCache[id] as any).hlsError = data;
// 					res(true); // resolves as error saved in cache
// 				});
// 			} else if (video.canPlayType('application/vnd.apple.mpegurl')) {
// 				console.info('hls not supported: using only video element');
// 				// populate error cache and resolve
// 				video.src = url;
// 				video.addEventListener('error', () => {
// 					errorCache[id] = new Error('failed to load video');
// 					res(true) // resolves as error saved in cache
// 				});
// 			}
// 			hlsVideoCache[id] = [
// 				new VideoTexture(video, undefined, undefined, undefined, undefined, undefined, RGBAFormat),
// 				video, 
// 				hls
// 			];
// 			// resolve promise when video playable, using loadedmetadata as canplay
// 			// event not fired in safari
// 			video.addEventListener('loadedmetadata', () => {
// 				hls?.stopLoad?.();
// 				res(true);
// 			});
// 		}));
// 	}
// 	return Promise.all(promises)
// }
